import { useRef, useState } from "react";
import ICONS from "../../../../../../assets/Icons";
import { linkIdentifier } from "../../../../../../utils/linkIdentifier";

import CommonBankLogo from "../../../../CommonBankLogo";
import PrimaryWithIconButton from "../../../../CommonButtons/PrimaryWithIconButton";
import SVGComponent from "../../../../SVGComponent";
import TickerAnimation from "../../../../TickerAnimation";

import "./index.css";
import { IntervalConfiguration } from "../../../../../../utils/configurationProvider";

// Suggestions of props for component while using the components -------------------------------------

/**
 * Click handler function
 * @typedef {((e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => void)} OnClickEventHandler
 */

/**
 * Row data structure for InfoCard content
 * @typedef {Object} InfoCardRow
 *
 * @property {string} [leftTitle] - Primary text on the left side
 * @property {string} [leftSubTitle] - Secondary text below left title
 *
 * @property {string} [rightTitle] - Primary text on the right side
 * @property {string} [rightSubTitle] - Secondary text below right title
 * @property {string} [rightTextFontWeight] - Font weight for right title
 * @property {string} [rightTitleTextColor] - Color of right title text
 *
 * @property {string} [icon] - Icon URL to display in the row
 * @property {string} [iconColor] - Icon color
 *
 * @property {string} [ifsc] - IFSC code to render bank logo
 *
 * @property {boolean} [rightIcon] - Shows arrow icon on the right
 * @property {OnClickEventHandler} [onClick] - Click handler for right icon
 */

/**
 * Props for InfoCard Component
 * @typedef {Object} InfoCardProps
 *
 * ---------- Card ----------
 * @property {string} [bgColor] - Background color of the card
 * @property {"white"|"black"|boolean} [hoverable] - Enables hover effect
 * @property {OnClickEventHandler} [onClick] - Click handler for entire card
 *
 * ---------- Header ----------
 * @property {boolean} [header] - Show header section
 * @property {string} [borderColor] - Header bottom border color
 * @property {string} [leftHeaderText] - Left header text
 * @property {string} [leftHeaderFontWeight] - Font weight for left header
 *
 * @property {boolean} [showTopArrow] - Show arrow icon in header
 *
 * @property {string} [rightHeaderText] - Right header text
 * @property {string} [rightHeaderFontWeight] - Font weight for right header
 * @property {string} [rightHeaderTextColor] - Right header text color
 * @property {string} [rightHeaderTextSize] - Right header text size
 *
 * ---------- Content ----------
 * @property {InfoCardRow[]} [rows] - Array of row objects to render card content
 * @property {boolean} [showBottomArrow] - Show arrow between left and right content
 * @property {string} [rightText] - Enables right content layout
 *
 * ---------- Button ----------
 * @property {boolean} [button] - Show bottom action button
 * @property {string} [enabledBgColor] - Button background color
 * @property {boolean} [isDisabled] - Disable button state
 * @property {OnClickEventHandler} [handleFunction] - Button click handler
 * @property {string} [enabledTextColor] - Button text color
 * @property {"white"|"black"} [hoverOption] - Button hover style
 * @property {boolean} [isLoading] - Show button loader
 * @property {string} [outLineColor] - Button outline color
 */

const InfoCard = ({
  /* Card */
  bgColor,
  hoverable,
  onClick,

  /* Header */
  header,
  borderColor,
  leftHeaderText,
  leftHeaderTextColor,
  leftHeaderFontWeight,
  leftHeaderTextSize,
  showTopArrow,
  rightHeaderText,
  rightHeaderFontWeight,
  rightHeaderTextColor,
  rightHeaderTextSize,

  /* MULTIPLE ROWS */
  rows = [],
  showRowDivider,
  rowBorderColor,

  showBottomArrow,
  rightText,

  /* Button Visibility */
  button,

  /* Button */
  enabledBgColor,
  isDisabled,
  handleFunction,
  enabledTextColor,
  hoverOption,
  isLoading,
  outLineColor,
}) => {
  const [tooltip, setTooltip] = useState({});
  const copyTimeoutRefs = useRef({});

  const appliedHoverBg = !hoverable
    ? ""
    : hoverable === "white"
      ? "global-hover-white"
      : "global-hover-black";

  // Header width
  const headerWidth = showTopArrow
    ? "Qlg:w-status-text-width w-width-md"
    : "flex-1 min-w-min-width-base";
  const leftHeaderWidth =
    !showTopArrow && !rightHeaderText
      ? "flex-1 min-w-min-width-base"
      : `${headerWidth}`;

  // Content width
  const contentWidth = showBottomArrow
    ? "Qlg:w-status-text-width w-width-md"
    : "Qlg:w-[var(--content-with)] w-width-md";
  const leftContentWidth =
    !showBottomArrow && !rightText
      ? "flex-1 min-w-min-width-base"
      : contentWidth;

  const ROW_ICON_SIZE = {
    small: "var(--icon-md)",
    big: "var(--icon-lg)",
  };

  const handleCopy = async (value, index) => {
    try {
      await navigator.clipboard.writeText(value);

      setTooltip((prev) => ({ ...prev, [index]: "Copied" }));

      clearTimeout(copyTimeoutRefs.current[index]);

      copyTimeoutRefs.current[index] = setTimeout(() => {
        setTooltip((prev) => ({ ...prev, [index]: "Copy to clipboard" }));
      }, IntervalConfiguration?.intervalMedium);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <div
        onClick={onClick}
        className={` ${appliedHoverBg}
          z-z-index-base 
          relative rounded-border-radius-md  mb-margin-sm last:mb-margin-base
          ${onClick ? "cursor-pointer" : "cursor-default"}`}
        style={{
          backgroundColor: bgColor,
        }}
      >
        {/* Header Section  */}
        {header && (
          <div style={{ borderBottom: `var(--border) solid ${borderColor}` }}>
            <div className="py-padding-xs px-padding-md flex items-center gap-gap-sm">
              <div
                className={`${leftHeaderWidth} overflow-hidden
                  whitespace-nowrap text-left ${
                    leftHeaderText === "Amount"
                      ? "font-normal"
                      : `font-${leftHeaderFontWeight}`
                  }`}
                style={{
                  color:
                    leftHeaderText === "Amount"
                      ? "var(--text-secondary)"
                      : "var(--text-primary)",
                  fontSize:
                    leftHeaderText === "Amount"
                      ? "var(--font-xs)"
                      : "var(--font-md)",
                }}
              >
                <TickerAnimation text={leftHeaderText} />
              </div>
              {showTopArrow && (
                <SVGComponent
                  url={ICONS?.ARROW_RIGHT_GREY}
                  width="var(--icon-sm)"
                  height="var(--icon-sm)"
                  color="var(--text-primary)"
                />
              )}

              {rightHeaderText && (
                <div
                  className={`${leftHeaderWidth} overflow-hidden
                  whitespace-nowrap text-right font-normal`}
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "var(--font-xs)",
                  }}
                >
                  <TickerAnimation text={rightHeaderText} />
                </div>
              )}
            </div>
          </div>
        )}
        {/* Header Section  */}

        {/* Content Section  */}
        <div
          className={`px-padding-md pt-padding-sm ${button ? "" : "pb-padding-sm"}`}
        >
          <div className="flex flex-col">
            {rows.map((row, index) => {
              const hasLeft = row.leftTitle || row.leftSubTitle;
              const hasRight = row.rightTitle || row.rightSubTitle;

              const isLastRow = index === rows.length - 1;
              const showDividerForThisRow =
                showRowDivider && (!isLastRow || button);

              const rowTooltip = tooltip[index] || "Copy to clipboard";

              return (
                <div
                  key={index}
                  className={`flex ${row.topRightIcon && row.rightIcon ? "mt-margin-xs  items-start" : "items-center"}  gap-gap-sm ${
                    showDividerForThisRow
                      ? "pb-padding-sm mb-margin-sm"
                      : "mb-margin-sm last:mb-margin-base"
                  }`}
                  style={
                    showDividerForThisRow
                      ? {
                          borderBottom: `var(--border) solid ${rowBorderColor}`,
                        }
                      : {}
                  }
                >
                  {/* LEFT */}
                  {hasLeft && (
                    <div
                      className={`${leftContentWidth}
                        overflow-hidden
                        whitespace-nowrap
                        flex flex-col gap-gap-xs items-start`}
                    >
                      <div
                        className={`text-left text-${row.leftTitleTextFontSize || "font-sm"} w-full font-${row.leftTextFontWeight}`}
                        style={{
                          color: !row.hasLink ? "var(--text-primary)" : "",
                        }}
                      >
                        {row.hasLink ? (
                          linkIdentifier(row.leftTitle)
                        ) : (
                          <TickerAnimation text={row.leftTitle} />
                        )}
                      </div>

                      {row.leftSubTitle && (
                        <div className="text-text-secondary text-font-xs font-normal">
                          {row.leftSubTitle}
                        </div>
                      )}
                    </div>
                  )}

                  {/* MIDDLE ARROW */}
                  {showBottomArrow && hasLeft && hasRight && (
                    <SVGComponent
                      url={ICONS?.ARROW_RIGHT_GREY}
                      width="var(--icon-sm)"
                      height="var(--icon-sm)"
                      color="var(--text-primary)"
                    />
                  )}

                  {/* RIGHT */}
                  {hasRight && row.rightText && (
                    <div
                      className={`${contentWidth}
                        overflow-hidden
                        whitespace-nowrap
                        flex flex-col gap-gap-xs items-end`}
                    >
                      <div
                        className={`w-full text-right text-font-sm font-${row.rightTextFontWeight}`}
                        style={{
                          color:
                            row.rightTitle === "Amount"
                              ? "var(--text-secondary)"
                              : row.rightTitleTextColor,
                        }}
                      >
                        <TickerAnimation text={row.rightTitle} />
                      </div>

                      {row.rightSubTitle && (
                        <div className="text-text-secondary text-font-xs font-normal">
                          {row.rightSubTitle}
                        </div>
                      )}
                    </div>
                  )}

                  {/* CATEGORY ICON */}
                  {row.icon && (
                    <div
                      className={`${row?.bgNeeded !== false ? "bg-text-primary p-padding-xs rounded-border-radius-sm" : ""}   flex-shrink-0`}
                    >
                      <SVGComponent
                        url={row.icon}
                        color={row.iconColor}
                        width={
                          ROW_ICON_SIZE[row.iconSize] || ROW_ICON_SIZE?.big
                        }
                        height={
                          ROW_ICON_SIZE[row.iconSize] || ROW_ICON_SIZE?.big
                        }
                      />
                    </div>
                  )}

                  {/* IFSC */}
                  {row.ifsc && (
                    <div className="flex flex-row gap-gap-sm items-center">
                      <CommonBankLogo
                        variant="big"
                        ifsc={row.ifsc}
                        type="pill"
                      />
                    </div>
                  )}

                  {/* RIGHT ARROW ICON */}
                  {row.rightIcon && (
                    <button
                      type="button"
                      // onClick={(e) => {
                      //   e.stopPropagation();
                      //   row.onClick?.(e);
                      // }}
                      className="cursor-pointer flex-shrink-0"
                    >
                      <SVGComponent
                        url={ICONS?.ARROW_RIGHT_WHITE}
                        color="var(--text-primary)"
                        width="var(--icon-sm)"
                        height="var(--icon-sm)"
                      />
                    </button>
                  )}

                  {/* Copy  */}
                  {row.copyText && (
                    <div
                      key={`copy-${index}-${rowTooltip}`}
                      className="bg-positive-bg hover:bg-positive-bg-hover rounded-border-radius-sm p-padding-xs cursor-pointer "
                      title={rowTooltip}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(row.copyText, index);
                      }}
                    >
                      <img
                        src={
                          rowTooltip === "Copied"
                            ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                            : ICONS?.COPY
                        }
                        alt="Copy"
                        className="w-icon-md h-icon-md"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content Section  */}

        {/* Button Section  */}
        {button && (
          <div className="w-full px-padding-md pb-padding-sm">
            <PrimaryWithIconButton
              btnText="Send for recreation"
              enabledBgColor={enabledBgColor}
              isDisabled={isDisabled}
              fullWidthRequired={true}
              icon={ICONS?.RECREATE_WHITE}
              imgColor="var(--text-primary)"
              iconToBeAlign="left"
              handleFunction={handleFunction}
              enabledTextColor={enabledTextColor}
              hoverOption={hoverOption}
              isLoading={isLoading}
              outLineColor={outLineColor}
            />
          </div>
        )}
        {/* Button Section  */}
      </div>
    </>
  );
};

export default InfoCard;
